import { ReactElement } from "react";

import { Button } from "react-bulma-components";
import styled from "styled-components";

import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { PreparedRouteApproval } from "containers/Applications/Applications";
import { showCommentInHTML } from "helpers/comment";
import * as Table from "molecules/Table";

export const MODE = {
  approve: "approve",
  deny: "deny",
} as const;

const Title = styled.h4`
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const renderCompletedMessage = (actionName: string) => (
  <div>
    <Title className="title is-4">
      以下の通勤経路申請を
      {actionName}
      しました
    </Title>
  </div>
);

export interface RouteApprovalDialogProps {
  isVisible: boolean;
  isUpdateCompleted: boolean;
  isUpdateInProgress: boolean;
  preparedRouteApproval?: PreparedRouteApproval;
  comment?: string;
  mode?: string;
  toggleDialog: () => void;
  changeComment: (comment: string) => void;
  updateToApprove: () => void;
  updateToDeny: () => void;
}

export function RouteApprovalDialog({
  isVisible,
  isUpdateCompleted,
  isUpdateInProgress,
  preparedRouteApproval,
  comment,
  mode,
  toggleDialog,
  changeComment,
  updateToApprove,
  updateToDeny,
}: RouteApprovalDialogProps): ReactElement | null {
  if (
    !preparedRouteApproval ||
    !preparedRouteApproval.preparedRoute ||
    !preparedRouteApproval.preparedRoute.user ||
    !mode
  ) {
    return null;
  }

  const modalCardTitle =
    mode === MODE.approve ? "通勤経路承認" : "通勤経路否認";
  const actionName = mode === MODE.approve ? "承認" : "否認";
  const buttonAction = mode === MODE.approve ? updateToApprove : updateToDeny;

  return (
    <div className={`modal ${isVisible ? "is-active" : ""}`}>
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">{modalCardTitle}</p>
          <button
            type="button"
            disabled={isUpdateInProgress}
            className="delete"
            aria-label="close"
            onClick={(_event) => toggleDialog()}
          />
        </header>
        <section className="modal-card-body">
          {isUpdateCompleted && renderCompletedMessage(actionName)}
          <Table.Table>
            <tbody>
              <tr>
                <Table.ThLarge>ID</Table.ThLarge>
                <Table.Td>
                  {preparedRouteApproval.preparedRoute.user.id}
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>申請者名</Table.ThLarge>
                <Table.Td>
                  {preparedRouteApproval.preparedRoute.user.nameJp}
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>申請ステータス</Table.ThLarge>
                <Table.Td>{preparedRouteApproval.approvalStatusName}</Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>到着地</Table.ThLarge>
                <Table.Td>
                  {preparedRouteApproval.preparedRoute.arrivalName}
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>金額</Table.ThLarge>
                <Table.Td>
                  <PriceLabel
                    amount={preparedRouteApproval.preparedRoute.totalFare}
                  />
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>あなたのコメント</Table.ThLarge>
                <Table.Td>
                  {showCommentInHTML(preparedRouteApproval.approverComment)}
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>申請者のコメント</Table.ThLarge>
                <Table.Td>
                  {showCommentInHTML(preparedRouteApproval.applicantComment)}
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>コメント</Table.ThLarge>
                <Table.Td>
                  {isUpdateCompleted ? (
                    <span>{comment}</span>
                  ) : (
                    <textarea
                      className="textarea"
                      onChange={(event) => {
                        changeComment(event.target.value);
                      }}
                      value={comment}
                    />
                  )}
                </Table.Td>
              </tr>
            </tbody>
          </Table.Table>
        </section>
        <footer className="modal-card-foot">
          <Button disabled={isUpdateInProgress} onClick={() => toggleDialog()}>
            {isUpdateCompleted ? "閉じる" : "キャンセル"}
          </Button>
          {!isUpdateCompleted && (
            <Button
              disabled={isUpdateInProgress}
              color="primary"
              onClick={() => buttonAction()}
            >
              {actionName}
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
